import { NetworkStatus, useLazyQuery } from '@apollo/client';
import {
  commodityPageSize,
  ListSelectionStyled,
  TextWithEllipsis,
  useGroupedCommodities,
} from '@bechtel/client-common';
import { GET_COMMODITIES } from '@bechtel/client-gql';
import { Box, Button, ButtonSize, Checkbox, Icon, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import {
  CommodityList as Commodities,
  CommodityFilterByTerm,
  CommodityFilterInput,
  CommodityMilestone,
  CommoditySortByTerm,
  FilterOperator,
  PagingInput,
  SortOrder,
  WorkPackage,
} from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { FieldArray } from 'formik';
import { findIndex, get, map, some } from 'lodash';
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Commodity } from '~/types';
import { CommodityListStyled } from './styled';

interface MultiCommodityListProps {
  workPackageId: WorkPackage['id'];
  selectedCommodityTypeId: string;
}

export const MultiCommodityList: FC<MultiCommodityListProps> = ({ workPackageId, selectedCommodityTypeId }) => {
  const [totalCommodities, setTotalCommodities] = useState(0);
  const observerRef = useRef<HTMLDivElement>(null);

  const [loadCommodities, { loading, error, data, refetch, networkStatus, fetchMore }] = useLazyQuery<
    { commodityList: Commodities },
    {
      input: CommodityFilterInput;
      paging: PagingInput;
    }
  >(GET_COMMODITIES, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    loadCommodities({
      variables: {
        input: {
          complexFilter: {
            and: [
              { term: CommodityFilterByTerm.WORK_PACKAGE_ID, value: workPackageId, operator: FilterOperator.EQUALS },
              {
                term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
                value: selectedCommodityTypeId,
                operator: FilterOperator.EQUALS,
              },
              { term: CommodityFilterByTerm.EXCLUDE_CONNECTED_COMMODITY, value: 'true', operator: FilterOperator.EQUALS },
            ],
          },
          sortByFilter: {
            term: CommoditySortByTerm.TAG,
            sortOrder: SortOrder.ASC,
          },
        },
        paging: { cursor: '1', limit: commodityPageSize },
      },
      notifyOnNetworkStatusChange: true,
    })
  }, [])
  const commodities = useMemo(() => get(data, 'commodities.items', []), [data]);

  const groupedCommodities = useGroupedCommodities(commodities);

  const filteredCommodities = groupedCommodities ? groupedCommodities[selectedCommodityTypeId] : [];
  const cursor = get(data, 'commodities.cursor', '0');
  const commodityCount = get(data, 'commodities.total');
  if (commodityCount && commodityCount !== totalCommodities) {
    setTotalCommodities(commodityCount);
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && commodities?.length <= totalCommodities && !loading) {
          fetchMore({
            variables: {
              paging: { cursor: cursor, limit: commodityPageSize },
            },
          });
        }
      },
      {
        root: null, // Use the viewport as the root
        rootMargin: '368px', // Trigger 200px before reaching the bottom
        threshold: 0.1, // Trigger when 10% of the target is visible
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [cursor, totalCommodities, loading, fetchMore]);


  if (error) {
    return (
      <Box css="height: 100%;" justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
        <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
          <Text>
            <Trans>There was a problem loading the commodities.</Trans>
          </Text>
          <Text>
            <Trans>Please click the button below to try again.</Trans>
          </Text>
        </Box>
        <Box>
          <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }
  return (
    <CommodityListStyled>
      <FieldArray name="commodityList">
        {({ push, remove, form }) => {
          const commodityList: Commodity[] = form.values.commodityList;

          return (
            <Fragment>
              {map(filteredCommodities, (commodity: Commodity) => {
                const selected = some(commodityList, { id: commodity.id });

                const onClick = () => {
                  if (selected) {
                    const index = findIndex(commodityList, { id: commodity.id });

                    remove(index);
                  } else {
                    const element = {
                      id: commodity.id,
                      tag: commodity.tag,
                      type: commodity.type,
                      milestones: map(commodity.milestones, (milestone: CommodityMilestone) => {
                        return { id: milestone.id, name: milestone.name };
                      }),
                    };

                    push(element);
                  }
                };

                return (
                  <ListSelectionStyled key={commodity.id} onClick={onClick}>
                    <Box mr={{ sm: '500' }}>
                      <Checkbox checked={selected} readOnly />
                    </Box>
                    <TextWithEllipsis family="graphik" size="400" color="grey.1000">
                      {commodity.tag}
                    </TextWithEllipsis>
                  </ListSelectionStyled>
                );
              })}
            </Fragment>
          );
        }}
      </FieldArray>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box
          css="height: 100%;"
          data-testid="loading"
          justifyContent={{ sm: 'center' }}
          alignItems={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {/* Sentinel element for IntersectionObserver */}
      {commodities?.length < totalCommodities && (
        <div ref={observerRef} style={{ height: '20px', width: '100%' }} />
      )}
    </CommodityListStyled>
  );
};
export default MultiCommodityList;
